import type { SomeLogger } from '../types/global';

export default class LogoutExecutor {
  readonly LOGOUT_DATA_SELECTOR = '.js-logout-data';

  readonly URL_ESHOP_ATTR = 'data-url-eshoplogout';

  readonly URL_LOGOUT = '/system/UI/LogoutPopup/Logout';

  readonly AUTH_TOKEN_STORAGE_KEY = 'AuthToken';

  readonly ININCHAT_ID_STORAGE_KEY = 'ininChatId';

  logger: SomeLogger;

  private readonly storage: Storage;

  constructor(logger: SomeLogger = console) {
    this.logger = logger;
    this.storage = window.sessionStorage;
  }

  doLogout(): void {
    this.clearSessionStorage();
    this.logoutInInChat();
    this.logoutFromEshop();
  }

  private clearSessionStorage(): void {
    const { ori } = window;
    if (!ori) {
      this.logger.warn(`[LogoutExecutor.clearSessionStorage] ori namespace not found on window!`);

      return;
    }

    if (ori.webApi) {
      const keys = ori.webApi.getStorageKeys();
      keys.forEach((item) => {
        sessionStorage.removeItem(item);
      });
    }

    if (ori.globalApi) {
      // clear globalAPI module
      ori.globalApi.clearStorageItems();
    }

    if (ori.personalMenu) {
      ori.personalMenu.clearStorageItems();
    }

    if (ori.allReportsPage) {
      ori.allReportsPage.clearStorageItems();
    }

    if (this.storage.getItem(this.AUTH_TOKEN_STORAGE_KEY)) {
      this.storage.removeItem(this.AUTH_TOKEN_STORAGE_KEY);
    }
  }

  private logoutInInChat(): void {
    const { ori } = window;
    if (!ori) {
      this.logger.warn(`[LogoutExecutor.logoutInInChat] ori namespace not found on window!`);

      return;
    }

    const ininChatStorage = this.storage.getItem(this.ININCHAT_ID_STORAGE_KEY);
    if (ori.ininChat && ininChatStorage) {
      this.logger.debug(`[LogoutExecutor.logoutInInChat] Closing InInChat...`);
      ori.ininChat.handleClose();
    }
  }

  private logoutFromEshop(): void {
    const logoutDataElement = document.querySelector(this.LOGOUT_DATA_SELECTOR);
    if (!logoutDataElement) {
      const errorMessage = `[LogoutExecutor.logoutFromEshop] Element ${this.LOGOUT_DATA_SELECTOR} was not found.`;
      this.logger.error(errorMessage);
      throw new Error(errorMessage);
    }

    const urlEshopLogout = logoutDataElement.getAttribute(this.URL_ESHOP_ATTR) ?? '';
    this.logger.debug(
      `[LogoutExecutor.logoutFromEshop] Logging out from eshop using: ${urlEshopLogout}`,
    );

    fetch(urlEshopLogout, { mode: 'no-cors', credentials: 'include' })
      .then(() => {
        this.logger.info(
          `[LogoutExecutor.logoutFromEshop] Logged out, redirecting to: ${this.URL_LOGOUT}`,
        );
        // eslint-disable-next-line no-restricted-globals -- The user needs to be redirected to the logout url
        location.href = this.URL_LOGOUT;
      })
      .catch((error: unknown) => {
        this.logger.error('[LogoutExecutor.logoutFromEshop] Error while logging out from eshop. ', {
          url: urlEshopLogout,
          error,
        });
      });
  }
}
